import React from 'react';
import { App } from '@meronex/app';

import { useTheme, useMediaQuery } from '@material-ui/core';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';

import IStores from '@meronex/icons/mdc/MdcWarehouse';
import IArrowRight from '@meronex/icons/md/MdChevronRight';
import IIn from '@meronex/icons/bi/BiImport';
import IOut from '@meronex/icons/bi/BiExport';
import IItem from '@meronex/icons/mdc/MdcPackageVariantClosed';
import ISwap from '@meronex/icons/mdc/MdcSwapHorizontal';
import IDollar from '@meronex/icons/fa/FaDollarSign';

import { sitesMng } from '../../sites/api';
import { storesMng } from '../../stores/api/storesMng';
import { can, p } from '../../common/roles';

const txConfig = {
  in: { icon: IIn, color: '#4caf50', label: 'In' },
  out: { icon: IOut, color: '#f44336', label: 'Out' },
  transferIn: { icon: IIn, color: '#2196f3', label: 'Transfer In' },
  transferOut: { icon: IOut, color: '#ff9800', label: 'Transfer Out' },
  modify: { icon: IOut, color: '#9e9e9e', label: 'Modified' },
  reverse: { icon: IOut, color: '#9e9e9e', label: 'Reversed' },
};

const typeColors = {
  in: '#4caf50',
  out: '#f44336',
  transferIn: '#2196f3',
  transferOut: '#ff9800',
  modify: '#9e9e9e',
  reverse: '#757575',
};

const typeLabels = {
  in: 'Stock In',
  out: 'Stock Out',
  transferIn: 'Transfer In',
  transferOut: 'Transfer Out',
  modify: 'Modify',
  reverse: 'Reverse',
};

function timeAgo(date) {
  const now = new Date();
  const d = new Date(date);
  const diffMs = now - d;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatDay(dayStr) {
  const d = new Date(dayStr + 'T00:00:00');
  return d.toLocaleDateString(undefined, { weekday: 'short' });
}

function ItemThumb({ thumbUrl, size = 30 }) {
  if (thumbUrl) {
    return (
      <img
        src={thumbUrl}
        alt=""
        style={{
          width: size,
          height: size,
          borderRadius: 6,
          objectFit: 'cover',
          flexShrink: 0,
        }}
      />
    );
  }
  return (
    <div style={{
      width: size,
      height: size,
      borderRadius: 6,
      backgroundColor: '#e0e0e0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}>
      <IItem style={{ fontSize: size * 0.55, color: '#999' }} />
    </div>
  );
}

export default function MngMenu(props) {
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';
  const isMobile = useMediaQuery('(max-width:600px)');
  const [, setSites] = React.useState([]);
  const [stores, setStores] = React.useState(null);
  const [dashData, setDashData] = React.useState(null);
  const [dashPeriod, setDashPeriod] = React.useState(30);
  const [userLoaded, setUserLoaded] = React.useState(false);

  const fetchSites = async () => {
    try {
      const sites = await sitesMng.getSites();
      setSites(sites);
      App.eventsManager.emit('event-sites-loaded', MngMenu.name, sites);
    } catch (e) {
      console.log('MngMenu: fetchSites error:', e);
    }
  };

  const fetchStores = async () => {
    try {
      const result = await storesMng.getStores();
      const storesArr = Array.isArray(result) ? result : [];
      setStores(storesArr);
      return storesArr;
    } catch (e) {
      console.log('MngMenu: fetchStores error:', e);
      setStores([]);
      return [];
    }
  };

  const fetchDashboard = async (days) => {
    try {
      setDashLoading(true);
      const data = await storesMng.getDashboardData({ days: days || dashPeriod });
      setDashData(data);
    } catch (e) {
      console.log('MngMenu: fetchDashboard error:', e);
      setDashData(null);
    } finally {
      setDashLoading(false);
    }
  };

  const saveReorderLevel = async (itemId, value) => {
    const num = parseFloat(value);
    if (isNaN(num) || num < 0) return;
    setSavingReorder(itemId);
    try {
      await storesMng.updateStoreItemThreshold({ storeItemId: itemId, lowStockThreshold: num });
      // Update local state
      setDashData((prev) => {
        if (!prev) return prev;
        const updated = prev.reorderItems.map((r) =>
          r._id === itemId ? { ...r, reorderLevel: num } : r
        ).filter((r) => r.quantity > r.reorderLevel ? false : true); // remove if now above level
        return { ...prev, reorderItems: updated };
      });
      setEditingReorder(null);
    } catch (e) {
      console.error('Failed to update reorder level:', e);
    } finally {
      setSavingReorder(null);
    }
  };

  const onUserLoaded = () => {
    setUserLoaded(true);
  };

  React.useEffect(() => {
    App.eventsManager.on('event-user-loaded', 'MngMenu', onUserLoaded);
    return () => {
      App.eventsManager.removeListener(
        'event-user-loaded',
        'MngMenu',
        onUserLoaded
      );
    };
  }, []);

  React.useEffect(() => {
    const init = async () => {
      fetchSites();
      fetchStores();
      fetchDashboard();
    };
    init();
    App.eventsManager.on('event-connected', MngMenu, () => {
      init();
    });
    return () => {
      App.eventsManager.removeListener('event-connected', MngMenu);
    };
  }, []);

  const setActiveView = (view) => {
    App.eventsManager.emit('event-active-view', MngMenu.name, {
      activeView: view,
    });
  };

  const goToStore = (store) => {
    storesMng.setUserSelectedStore(store._id);
    setActiveView('stores');
  };

  const [user, setUser] = React.useState(null);

  React.useEffect(() => {
    setUser(App.getUser());
  }, [userLoaded]);

  const hasStores = can(p.globals.accessStores);

  const totalItems = stores
    ? stores.reduce((sum, s) => sum + (s.itemCount || 0), 0)
    : 0;
  const totalTransactions = stores
    ? stores.reduce((sum, s) => sum + (s.transactionsCount || 0), 0)
    : 0;
  const totalTransactions30d = dashData?.storeVolume
    ? Object.values(dashData.storeVolume).reduce((sum, v) => sum + (v || 0), 0)
    : null;

  const cardBg = isDark ? '#2a2a2e' : '#ffffff';
  const cardBorder = isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0';
  const cardShadow = isDark ? 'none' : '0 1px 2px 0 rgba(0,0,0,0.05)';
  const cardHoverShadow = isDark ? 'none' : '0 4px 6px -1px rgba(0,0,0,0.1)';
  const textColor = isDark ? '#e0e0e0' : '#222';
  const subColor = isDark ? '#999' : '#888';
  const sectionTitle = isDark ? '#bbb' : '#555';

  const recentTx = dashData?.recentTransactions || null;
  const dailyVolume = dashData?.dailyVolume || [];
  const typeBreakdown = dashData?.typeBreakdown || {};
  const topItems = dashData?.topItems || [];
  const reorderItems = dashData?.reorderItems || [];
  const mostConsumed = dashData?.mostConsumed || [];
  const topByValue = dashData?.topByValue || [];
  const slowMoving = dashData?.slowMoving || [];
  const inventoryValue = dashData?.inventoryValue || {};
  const totalInventoryValue = dashData?.totalInventoryValue || 0;
  const storeVolume = dashData?.storeVolume || {};

  const [dashLoading, setDashLoading] = React.useState(false);
  const [itemsTab, setItemsTab] = React.useState('top');
  const itemsTabUserPicked = React.useRef(false);
  const setItemsTabExplicit = React.useCallback((tab) => {
    itemsTabUserPicked.current = true;
    setItemsTab(tab);
  }, []);

  // Default to first tab with data when dashData arrives (skip if user already picked one).
  React.useEffect(() => {
    if (!dashData || itemsTabUserPicked.current) return;
    const candidates = [
      ['top', topItems.length],
      ['consumed', mostConsumed.length],
      ['value', topByValue.length],
      ['slow', slowMoving.length],
      ['reorder', reorderItems.length],
    ];
    const firstNonEmpty = candidates.find(([, n]) => n > 0);
    if (firstNonEmpty && firstNonEmpty[0] !== itemsTab) {
      setItemsTab(firstNonEmpty[0]);
    }
  }, [dashData, topItems.length, mostConsumed.length, topByValue.length, slowMoving.length, reorderItems.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const [consumedSort, setConsumedSort] = React.useState('value');
  const [topItemsPage, setTopItemsPage] = React.useState(0);
  const TOP_ITEMS_PER_PAGE = 10;
  const [editingReorder, setEditingReorder] = React.useState(null); // item._id being edited
  const [editReorderVal, setEditReorderVal] = React.useState('');
  const [savingReorder, setSavingReorder] = React.useState(null);

  // Compute max for type breakdown bars
  const tbValues = Object.values(typeBreakdown);
  const tbMax = tbValues.length > 0 ? Math.max(...tbValues) : 1;

  const greeting = (() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  })();
  const firstName = user?.profile?.firstName || user?.profile?.name?.split(' ')[0] || '';
  const todayStr = new Date().toLocaleDateString(undefined, {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });

  const formatValue = (val) => {
    if (!val || val === 0) return '—';
    if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`;
    if (val >= 1000) return `${(val / 1000).toFixed(1)}K`;
    return val.toFixed(0);
  };

  const formatCount = (val) => {
    if (!val || val === 0) return '0';
    if (val >= 1000) return `${(val / 1000).toFixed(1)}K`;
    return val.toLocaleString();
  };

  const skFrom = isDark ? '#2a2a2e' : '#e2e8f0';
  const skTo = isDark ? '#3a3a40' : '#f1f5f9';
  const Sk = ({ w, h, r = 8, style = {}, delay = 0 }) => (
    <div style={{
      width: w, height: h, borderRadius: r,
      backgroundColor: skFrom,
      animation: `skPulse 2s ease-in-out ${delay}s infinite`,
      ...style,
    }} />
  );

  const isLoading = !user && !userLoaded;

  if (!isLoading && !hasStores) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
        No modules available
      </div>
    );
  }

  return (
    <div style={{ padding: '4px 0', overflow: 'hidden' }}>
      <style>{`
        @keyframes skPulse {
          0%, 100% { background-color: ${skFrom}; }
          50% { background-color: ${skTo}; }
        }
      `}</style>
      {/* Welcome header */}
      <div style={{ padding: '8px 8px 24px' }}>
        {isLoading ? (
          <>
            <Sk w="240px" h="34px" r={6} />
            <Sk w="160px" h="14px" r={4} style={{ marginTop: 4 }} delay={0.1} />
          </>
        ) : (
          <>
            <div style={{ fontSize: '28px', fontWeight: 700, color: isDark ? '#e2e8f0' : '#0f172a' }}>
              {greeting}{firstName ? `, ${firstName}` : ''}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '4px' }}>
              <div style={{ fontSize: '14px', color: isDark ? '#666' : '#94a3b8' }}>
                {todayStr}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '2px', backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9', borderRadius: '6px', padding: '2px' }}>
                {[
                  { key: 7, label: '7d' },
                  { key: 30, label: '30d' },
                  { key: 90, label: '90d' },
                ].map((opt) => (
                  <div
                    key={opt.key}
                    onClick={() => {
                      if (dashPeriod !== opt.key) {
                        setDashPeriod(opt.key);
                        setTopItemsPage(0);
                        fetchDashboard(opt.key);
                      }
                    }}
                    style={{
                      padding: '3px 10px',
                      borderRadius: '5px',
                      fontSize: '11px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      backgroundColor: dashPeriod === opt.key ? (isDark ? 'rgba(99,102,241,0.2)' : '#eef2ff') : 'transparent',
                      color: dashPeriod === opt.key ? (isDark ? '#a5b4fc' : '#4f46e5') : (isDark ? '#666' : '#94a3b8'),
                    }}
                  >
                    {opt.label}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Stats cards — 4-column grid (2x2 on mobile) */}
      <div style={{ position: 'relative' }}>
      {dashLoading && dashData && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10,
          backgroundColor: isDark ? 'rgba(30,30,34,0.5)' : 'rgba(255,255,255,0.5)',
          backdropFilter: 'blur(3px)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: '12px',
          pointerEvents: 'none',
        }}>
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            gap: '10px',
            padding: '24px 36px',
            borderRadius: '16px',
            backgroundColor: isDark ? 'rgba(30,30,34,0.95)' : 'rgba(255,255,255,0.95)',
            boxShadow: isDark
              ? '0 8px 32px rgba(0,0,0,0.4)'
              : '0 8px 32px rgba(0,0,0,0.1)',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
          }}>
            <div style={{
              width: '32px', height: '32px',
              border: '3px solid', borderColor: isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0',
              borderTopColor: '#6366f1', borderRadius: '50%',
              animation: 'dashSpin 0.8s linear infinite',
            }} />
            <style>{`@keyframes dashSpin { to { transform: rotate(360deg); } }`}</style>
            <span style={{
              fontSize: '14px',
              fontWeight: 600,
              color: isDark ? '#e2e8f0' : '#334155',
            }}>
              Loading dashboard...
            </span>
          </div>
        </div>
      )}
      {stores && stores.length > 0 ? (
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
          gap: '12px',
          marginBottom: '20px',
          padding: '0 4px',
        }}>
          {[
            {
              label: 'Total Stores',
              value: stores.length,
              sub: 'Active locations',
              icon: <IStores size={20} />,
              iconBg: isDark ? 'rgba(99,102,241,0.15)' : '#e0e7ff',
              iconColor: '#4f46e5',
              border: isDark ? '1px solid rgba(99,102,241,0.2)' : '1px solid #e0e7ff',
            },
            {
              label: 'Total Items',
              value: totalItems.toLocaleString(),
              sub: 'In inventory',
              icon: <IItem size={20} />,
              iconBg: isDark ? 'rgba(239,68,68,0.15)' : '#ffe4e6',
              iconColor: '#e11d48',
              border: isDark ? '1px solid rgba(239,68,68,0.2)' : '1px solid #ffe4e6',
            },
            {
              label: 'Transactions',
              value: totalTransactions30d != null
                ? totalTransactions30d.toLocaleString()
                : totalTransactions.toLocaleString(),
              sub: totalTransactions30d != null ? `Last ${dashPeriod} days` : 'All time',
              icon: <ISwap size={20} />,
              iconBg: isDark ? 'rgba(16,185,129,0.15)' : '#d1fae5',
              iconColor: '#059669',
              border: isDark ? '1px solid rgba(16,185,129,0.2)' : '1px solid #d1fae5',
            },
            {
              label: 'Inventory Value',
              value: formatValue(totalInventoryValue),
              sub: 'Total valuation',
              icon: <IDollar size={18} />,
              iconBg: isDark ? 'rgba(245,158,11,0.15)' : '#fef3c7',
              iconColor: '#d97706',
              border: isDark ? '1px solid rgba(245,158,11,0.2)' : '1px solid #fef3c7',
            },
          ].map((stat, i) => (
            <div key={i} style={{
              backgroundColor: cardBg,
              borderRadius: '12px',
              border: stat.border,
              boxShadow: cardShadow,
              padding: isMobile ? '14px' : '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: isMobile ? '8px' : '12px',
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              }}>
                <div style={{ fontSize: isMobile ? '12px' : '13px', color: isDark ? '#999' : '#64748b', fontWeight: 500 }}>
                  {stat.label}
                </div>
                <div style={{
                  width: isMobile ? '36px' : '44px',
                  height: isMobile ? '36px' : '44px',
                  borderRadius: isMobile ? '10px' : '12px',
                  backgroundColor: stat.iconBg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: stat.iconColor,
                  flexShrink: 0,
                }}>
                  {stat.icon}
                </div>
              </div>
              <div>
                <div style={{ fontSize: isMobile ? '22px' : '30px', fontWeight: 700, color: textColor, lineHeight: 1.1 }}>
                  {stat.value}
                </div>
                <div style={{ fontSize: '13px', color: isDark ? '#777' : '#94a3b8', marginTop: '4px' }}>
                  {stat.sub}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
          gap: 12, padding: '0 4px', marginBottom: 20,
        }}>
          {[0, 0.08, 0.16, 0.24].map((d, i) => (
            <div key={i} style={{
              backgroundColor: cardBg, borderRadius: 12, border: cardBorder, padding: 20,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Sk w="80px" h="12px" r={4} delay={d} />
                <Sk w="44px" h="44px" r={12} delay={d + 0.05} />
              </div>
              <Sk w="90px" h="26px" r={6} delay={d + 0.1} style={{ marginTop: 16 }} />
              <Sk w="70px" h="10px" r={4} delay={d + 0.15} style={{ marginTop: 8 }} />
            </div>
          ))}
        </div>
      )}

      {/* Charts row — stacked on mobile, side by side on desktop */}
      {dashData ? (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          padding: '0 4px',
          marginBottom: '20px',
        }}>
          {/* Daily Activity Chart */}
          {dailyVolume.length > 0 && (
            <div style={{
              flex: '1 1 320px',
              minWidth: 0,
              backgroundColor: cardBg,
              borderRadius: '12px',
              boxShadow: cardShadow, border: cardBorder,
              padding: '16px 12px 8px',
            }}>
              <div style={{
                fontSize: '13px',
                fontWeight: 600,
                color: sectionTitle,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '12px',
                paddingLeft: '4px',
              }}>
                Daily Activity ({dashPeriod} days)
              </div>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={dailyVolume} margin={{ top: 0, right: 4, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#444' : '#eee'} />
                  <XAxis
                    dataKey="day"
                    tickFormatter={formatDay}
                    tick={{ fontSize: 11, fill: subColor }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: subColor }}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: isDark ? '#333' : '#fff',
                      border: 'none',
                      borderRadius: 8,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      fontSize: 12,
                    }}
                    labelFormatter={(v) => {
                      const d = new Date(v + 'T00:00:00');
                      return d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
                    }}
                  />
                  <Bar dataKey="in" stackId="a" fill="#4caf50" radius={[0, 0, 0, 0]} name="In" />
                  <Bar dataKey="out" stackId="a" fill="#f44336" radius={[4, 4, 0, 0]} name="Out" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Transaction Type Breakdown */}
          {tbValues.length > 0 && (
            <div style={{
              flex: '1 1 240px',
              minWidth: 0,
              backgroundColor: cardBg,
              borderRadius: '12px',
              boxShadow: cardShadow, border: cardBorder,
              padding: '16px 14px',
            }}>
              <div style={{
                fontSize: '13px',
                fontWeight: 600,
                color: sectionTitle,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '14px',
              }}>
                By Type ({dashPeriod} days)
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {Object.entries(typeBreakdown)
                  .sort((a, b) => b[1] - a[1])
                  .map(([type, count]) => (
                    <div key={type}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '4px',
                      }}>
                        <span style={{ fontSize: '12px', color: textColor, fontWeight: 500 }}>
                          {typeLabels[type] || type}
                        </span>
                        <span style={{ fontSize: '12px', color: subColor, fontWeight: 600 }}>
                          {count}
                        </span>
                      </div>
                      <div style={{
                        height: '6px',
                        borderRadius: '3px',
                        backgroundColor: isDark ? '#444' : '#f0f0f0',
                        overflow: 'hidden',
                      }}>
                        <div style={{
                          height: '100%',
                          width: `${(count / tbMax) * 100}%`,
                          backgroundColor: typeColors[type] || '#999',
                          borderRadius: '3px',
                          transition: 'width 0.3s ease',
                        }} />
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 12, padding: '0 4px', marginBottom: 20 }}>
          <div style={{
            flex: '1 1 320px', backgroundColor: cardBg, borderRadius: 12,
            border: cardBorder, padding: 0, overflow: 'hidden',
          }}>
            <div style={{ padding: '16px 20px 12px', display: 'flex', justifyContent: 'space-between' }}>
              <Sk w="130px" h="14px" r={4} delay={0.1} />
              <Sk w="80px" h="14px" r={4} delay={0.15} />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, padding: '8px 20px 20px', height: 120 }}>
              {[55, 80, 40, 65, 90, 50, 35].map((h, i) => (
                <Sk key={i} w="100%" h={`${h}%`} r={4} delay={0.05 * i} style={{ flex: 1 }} />
              ))}
            </div>
          </div>
          <div style={{
            flex: '1 1 260px', backgroundColor: cardBg, borderRadius: 12,
            border: cardBorder, padding: 20,
          }}>
            <Sk w="140px" h="14px" r={4} delay={0.1} />
            {[0.15, 0.2, 0.25, 0.3, 0.35, 0.4].map((d, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14 }}>
                <Sk w="60px" h="10px" r={3} delay={d} />
                <Sk w={`${[85, 55, 30, 20, 12, 8][i]}%`} h="8px" r={4} delay={d + 0.05} style={{ flex: 1 }} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bottom row — Top Items + Stores side by side */}
      <div style={{
        display: isMobile ? 'block' : 'grid',
        gridTemplateColumns: '2fr 1fr',
        gap: '12px',
        padding: '0 4px',
        marginBottom: '20px',
      }}>

      {/* Top Items */}
      {!dashData ? (
        <div style={{
          backgroundColor: cardBg, borderRadius: 12, border: cardBorder, padding: 20,
          marginBottom: isMobile ? 12 : 0,
        }}>
          <Sk w="120px" h="14px" r={4} style={{ marginBottom: 16 }} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10 }}>
            {[0, 0.05, 0.1, 0.15, 0.2].map((d, i) => (
              <div key={i} style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
                backgroundColor: isDark ? '#333' : '#f8fafc', borderRadius: 10, padding: 16,
              }}>
                <Sk w="56px" h="56px" r={10} delay={d} />
                <Sk w="75px" h="11px" r={3} delay={d + 0.05} />
                <Sk w="55px" h="10px" r={3} delay={d + 0.1} />
                <Sk w="100%" h="4px" r={2} delay={d + 0.15} />
              </div>
            ))}
          </div>
        </div>
      ) : (topItems.length > 0 || mostConsumed.length > 0 || topByValue.length > 0 || slowMoving.length > 0 || reorderItems.length > 0) ? (
        <div style={{
          backgroundColor: cardBg,
          borderRadius: '12px',
          border: cardBorder,
          boxShadow: cardShadow,
          padding: '20px',
          marginBottom: isMobile ? '12px' : 0,
        }}>
          {/* Tab header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
          }}>
            <div style={{ display: 'flex', gap: '2px', flexWrap: 'wrap' }}>
              {[
                { key: 'top', label: 'Most Active', count: topItems.length, desc: 'Ranked by transaction count (30 days)' },
                { key: 'consumed', label: 'Most Consumed', count: mostConsumed.length, desc: 'Ranked by consumption value (30 days)' },
                { key: 'value', label: 'Highest Value', count: topByValue.length, desc: 'Ranked by inventory value (qty × rate)' },
                { key: 'slow', label: 'Slow Moving', count: slowMoving.length, desc: 'In stock but ≤2 transactions (30 days)' },
                { key: 'reorder', label: 'Reorder', count: reorderItems.length, badge: true, desc: 'Below reorder level' },
              ].filter(t => t.count > 0).map((tab) => {
                const isActive = itemsTab === tab.key;
                const activeColors = {
                  top: { bg: isDark ? 'rgba(99,102,241,0.15)' : '#eef2ff', color: isDark ? '#a5b4fc' : '#4f46e5' },
                  consumed: { bg: isDark ? 'rgba(239,68,68,0.15)' : '#fef2f2', color: isDark ? '#fca5a5' : '#dc2626' },
                  value: { bg: isDark ? 'rgba(16,185,129,0.15)' : '#ecfdf5', color: isDark ? '#6ee7b7' : '#059669' },
                  slow: { bg: isDark ? 'rgba(156,163,175,0.15)' : '#f1f5f9', color: isDark ? '#9ca3af' : '#475569' },
                  reorder: { bg: isDark ? 'rgba(245,158,11,0.15)' : '#fef3c7', color: '#d97706' },
                };
                const c = activeColors[tab.key];
                return (
                  <div
                    key={tab.key}
                    onClick={() => { setItemsTabExplicit(tab.key); setTopItemsPage(0); }}
                    style={{
                      padding: '5px 12px',
                      borderRadius: '8px',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      backgroundColor: isActive ? c.bg : 'transparent',
                      color: isActive ? c.color : subColor,
                    }}
                  >
                    {tab.label}
                    {tab.badge && tab.count > 0 && (
                      <span style={{
                        marginLeft: '5px',
                        fontSize: '10px',
                        fontWeight: 700,
                        padding: '1px 5px',
                        borderRadius: '10px',
                        backgroundColor: isActive ? c.color : (isDark ? 'rgba(245,158,11,0.2)' : '#fde68a'),
                        color: isActive ? '#fff' : (isDark ? '#fbbf24' : '#92400e'),
                      }}>
                        {tab.count}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            <div style={{ fontSize: '11px', color: subColor, display: 'flex', alignItems: 'center', gap: '8px' }}>
              {/* Consumed sort toggle */}
              {itemsTab === 'consumed' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '2px', backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9', borderRadius: '6px', padding: '2px' }}>
                  {[
                    { key: 'value', label: 'By Value' },
                    { key: 'quantity', label: 'By Qty' },
                  ].map((opt) => (
                    <div
                      key={opt.key}
                      onClick={() => { setConsumedSort(opt.key); setTopItemsPage(0); }}
                      style={{
                        padding: '3px 10px',
                        borderRadius: '5px',
                        fontSize: '11px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        backgroundColor: consumedSort === opt.key ? (isDark ? 'rgba(239,68,68,0.2)' : '#fef2f2') : 'transparent',
                        color: consumedSort === opt.key ? (isDark ? '#fca5a5' : '#dc2626') : subColor,
                      }}
                    >
                      {opt.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Top Items grid */}
          {itemsTab === 'top' && (() => {
            const totalPages = Math.ceil(topItems.length / TOP_ITEMS_PER_PAGE);
            const start = topItemsPage * TOP_ITEMS_PER_PAGE;
            const visibleItems = topItems.slice(start, start + TOP_ITEMS_PER_PAGE);
            return (
              <div>
                <div key={`page-${topItemsPage}`} style={isMobile ? {
                  display: 'flex',
                  gap: '12px',
                  overflowX: 'auto',
                  WebkitOverflowScrolling: 'touch',
                  paddingBottom: '4px',
                } : {
                  display: 'grid',
                  gridTemplateColumns: 'repeat(5, 1fr)',
                  gap: '10px',
                }}>
                  {visibleItems.map((item, i) => {
                    const netChange = (item.totalIn || 0) - (item.totalOut || 0);
                    const maxVal = Math.max(item.totalIn || 0, item.totalOut || 0, 1);
                    const barPct = Math.min(Math.abs(netChange) / maxVal * 100, 100);
                    return (
                      <div
                        key={i}
                        style={{
                          backgroundColor: isDark ? '#333' : '#f8fafc',
                          borderRadius: '10px',
                          border: '1px solid transparent',
                          padding: '10px',
                          display: 'flex',
                          flexDirection: 'column',
                          position: 'relative',
                          transition: 'all 0.2s ease',
                          minWidth: 0,
                          overflow: 'hidden',
                          ...(isMobile ? { minWidth: '160px', flexShrink: 0 } : {}),
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = isDark ? '#3a3a3e' : '#fff';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                          e.currentTarget.style.border = isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e0e7ff';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = isDark ? '#333' : '#f8fafc';
                          e.currentTarget.style.boxShadow = 'none';
                          e.currentTarget.style.border = '1px solid transparent';
                        }}
                      >
                        {/* Trend badge */}
                        <div style={{
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          zIndex: 1,
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          backgroundColor: netChange >= 0 ? '#10b981' : '#ef4444',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                        }}>
                          {netChange >= 0 ? '↑' : '↓'}
                        </div>

                        {/* Image */}
                        <div style={{
                          width: '100%',
                          height: isMobile ? '120px' : '140px',
                          borderRadius: '8px',
                          backgroundColor: isDark ? '#2a2a2e' : '#fff',
                          overflow: 'hidden',
                          marginBottom: '10px',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                        }}>
                          {item.thumbUrl ? (
                            <img
                              src={item.thumbUrl}
                              alt={item.name}
                              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            />
                          ) : (
                            <div style={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}>
                              <IItem style={{ fontSize: 32, color: '#ccc' }} />
                            </div>
                          )}
                        </div>

                        {/* Name */}
                        <div style={{
                          fontSize: '13px',
                          fontWeight: 500,
                          color: textColor,
                          lineHeight: '1.3',
                          minHeight: '2.4em',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}>
                          {item.name}
                        </div>

                        {/* Stats */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          fontSize: '11px',
                          marginTop: '8px',
                          gap: '4px',
                          minWidth: 0,
                        }}>
                          <div style={{ whiteSpace: 'nowrap' }}>
                            <span style={{ color: '#10b981', fontWeight: 500 }}>+{item.totalIn || 0}</span>
                            <span style={{ color: subColor }}> / </span>
                            <span style={{ color: '#ef4444', fontWeight: 500 }}>-{item.totalOut || 0}</span>
                          </div>
                          <span style={{ color: subColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{item.unit || ''}</span>
                        </div>

                        {/* Transactions count + Net change bar */}
                        <div style={{ marginTop: '6px' }}>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontSize: '11px',
                            marginBottom: '3px',
                          }}>
                            <span style={{ color: subColor }}>{item.txCount} txns</span>
                            <span style={{
                              fontWeight: 600,
                              color: netChange >= 0 ? '#10b981' : '#ef4444',
                            }}>
                              {netChange >= 0 ? '+' : ''}{netChange}
                            </span>
                          </div>
                          <div style={{
                            height: '4px',
                            borderRadius: '2px',
                            backgroundColor: isDark ? '#444' : '#e2e8f0',
                            overflow: 'hidden',
                          }}>
                            <div style={{
                              height: '100%',
                              width: `${barPct}%`,
                              borderRadius: '2px',
                              backgroundColor: netChange >= 0 ? '#10b981' : '#ef4444',
                              marginLeft: netChange < 0 ? 'auto' : '0',
                            }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Pagination */}
                {totalPages > 1 && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 0 4px',
                  }}>
                    <div
                      onClick={() => topItemsPage > 0 && setTopItemsPage(topItemsPage - 1)}
                      style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: topItemsPage > 0 ? 'pointer' : 'default',
                        opacity: topItemsPage > 0 ? 1 : 0.3,
                        backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
                        color: isDark ? '#e2e8f0' : '#334155',
                        fontSize: '14px',
                        transition: 'background-color 0.15s',
                      }}
                      onMouseEnter={(e) => { if (topItemsPage > 0) e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'; }}
                    >
                      ‹
                    </div>
                    <span style={{ fontSize: '12px', color: subColor, fontWeight: 500 }}>
                      {start + 1}–{Math.min(start + TOP_ITEMS_PER_PAGE, topItems.length)} of {topItems.length}
                    </span>
                    <div
                      onClick={() => topItemsPage < totalPages - 1 && setTopItemsPage(topItemsPage + 1)}
                      style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: topItemsPage < totalPages - 1 ? 'pointer' : 'default',
                        opacity: topItemsPage < totalPages - 1 ? 1 : 0.3,
                        backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
                        color: isDark ? '#e2e8f0' : '#334155',
                        fontSize: '14px',
                        transition: 'background-color 0.15s',
                      }}
                      onMouseEnter={(e) => { if (topItemsPage < totalPages - 1) e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'; }}
                    >
                      ›
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Most Consumed grid */}
          {itemsTab === 'consumed' && (() => {
            const sorted = [...mostConsumed].sort((a, b) => consumedSort === 'value' ? (b.totalValue - a.totalValue) : (b.totalOut - a.totalOut));
            const totalPages = Math.ceil(sorted.length / TOP_ITEMS_PER_PAGE);
            const start = topItemsPage * TOP_ITEMS_PER_PAGE;
            const visible = sorted.slice(start, start + TOP_ITEMS_PER_PAGE);
            const maxMetric = sorted[0] ? (consumedSort === 'value' ? sorted[0].totalValue : sorted[0].totalOut) : 1;
            return (
              <div>
                <div key={`consumed-page-${topItemsPage}-${consumedSort}`} style={isMobile ? {
                  display: 'flex', gap: '12px', overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: '4px',
                } : {
                  display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px',
                }}>
                  {visible.map((item, i) => {
                    const badgeText = consumedSort === 'value'
                      ? (item.totalValue > 0 ? item.totalValue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : parseFloat(item.totalOut.toFixed(2)))
                      : parseFloat(item.totalOut.toFixed(2));
                    const barMetric = consumedSort === 'value' ? item.totalValue : item.totalOut;
                    return (
                    <div key={i} style={{
                      backgroundColor: isDark ? '#333' : '#f8fafc', borderRadius: '10px', border: '1px solid transparent',
                      padding: '10px', display: 'flex', flexDirection: 'column', position: 'relative', transition: 'all 0.2s ease',
                      minWidth: 0, overflow: 'hidden',
                      ...(isMobile ? { minWidth: '160px', flexShrink: 0 } : {}),
                    }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDark ? '#3a3a3e' : '#fff'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; e.currentTarget.style.border = isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e0e7ff'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isDark ? '#333' : '#f8fafc'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.border = '1px solid transparent'; }}
                    >
                      {/* Badge */}
                      <div style={{
                        position: 'absolute', top: '8px', right: '8px', zIndex: 1,
                        padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 700,
                        backgroundColor: '#dc2626', color: '#fff',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                      }}>{badgeText}</div>
                      {/* Image */}
                      <div style={{ width: '100%', height: isMobile ? '120px' : '140px', borderRadius: '8px', backgroundColor: isDark ? '#2a2a2e' : '#fff', overflow: 'hidden', marginBottom: '10px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                        {item.thumbUrl ? (
                          <img src={item.thumbUrl} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        ) : (
                          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <IItem style={{ fontSize: 32, color: '#ccc' }} />
                          </div>
                        )}
                      </div>
                      {/* Name */}
                      <div style={{ fontSize: '13px', fontWeight: 500, color: textColor, lineHeight: '1.3', minHeight: '2.4em', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {item.name}
                      </div>
                      {/* Stats */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px', marginTop: '8px', gap: '4px', minWidth: 0 }}>
                        <span style={{ color: subColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{consumedSort === 'value' ? `${item.totalValue.toLocaleString(undefined, { maximumFractionDigits: 0 })} val` : `${parseFloat(item.totalOut.toFixed(2))} ${item.unit}`}</span>
                        <span style={{ color: subColor, whiteSpace: 'nowrap', flexShrink: 0 }}>{item.txCount} txns</span>
                      </div>
                      {/* Bar */}
                      <div style={{ marginTop: '6px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '3px' }}>
                          <span style={{ color: subColor }}>{item.storeName}</span>
                        </div>
                        <div style={{ height: '4px', borderRadius: '2px', backgroundColor: isDark ? '#444' : '#e2e8f0', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${Math.min((barMetric / maxMetric) * 100, 100)}%`, borderRadius: '2px', backgroundColor: '#ef4444' }} />
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
                {totalPages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', padding: '12px 0 4px' }}>
                    <div onClick={() => topItemsPage > 0 && setTopItemsPage(topItemsPage - 1)} style={{ width: '28px', height: '28px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: topItemsPage > 0 ? 'pointer' : 'default', opacity: topItemsPage > 0 ? 1 : 0.3, backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9', color: isDark ? '#e2e8f0' : '#334155', fontSize: '14px' }}>‹</div>
                    <span style={{ fontSize: '12px', color: subColor, fontWeight: 500 }}>{start + 1}–{Math.min(start + TOP_ITEMS_PER_PAGE, sorted.length)} of {sorted.length}</span>
                    <div onClick={() => topItemsPage < totalPages - 1 && setTopItemsPage(topItemsPage + 1)} style={{ width: '28px', height: '28px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: topItemsPage < totalPages - 1 ? 'pointer' : 'default', opacity: topItemsPage < totalPages - 1 ? 1 : 0.3, backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9', color: isDark ? '#e2e8f0' : '#334155', fontSize: '14px' }}>›</div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Highest Value grid */}
          {itemsTab === 'value' && (() => {
            const totalPages = Math.ceil(topByValue.length / TOP_ITEMS_PER_PAGE);
            const start = topItemsPage * TOP_ITEMS_PER_PAGE;
            const visible = topByValue.slice(start, start + TOP_ITEMS_PER_PAGE);
            return (
              <div>
                <div key={`value-page-${topItemsPage}`} style={isMobile ? {
                  display: 'flex', gap: '12px', overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: '4px',
                } : {
                  display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px',
                }}>
                  {visible.map((item, i) => {
                    const valueFmt = item.value >= 1000 ? `${(item.value / 1000).toFixed(1)}K` : item.value.toFixed(0);
                    return (
                      <div key={item._id || i} style={{
                        backgroundColor: isDark ? '#333' : '#f8fafc', borderRadius: '10px', border: '1px solid transparent',
                        padding: '10px', display: 'flex', flexDirection: 'column', position: 'relative', transition: 'all 0.2s ease',
                        minWidth: 0, overflow: 'hidden',
                        ...(isMobile ? { minWidth: '160px', flexShrink: 0 } : {}),
                      }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDark ? '#3a3a3e' : '#fff'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; e.currentTarget.style.border = isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e0e7ff'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isDark ? '#333' : '#f8fafc'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.border = '1px solid transparent'; }}
                      >
                        {/* Value badge */}
                        <div style={{
                          position: 'absolute', top: '8px', right: '8px', zIndex: 1,
                          padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 700,
                          backgroundColor: '#059669', color: '#fff',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                        }}>{valueFmt}</div>
                        {/* Image */}
                        <div style={{ width: '100%', height: isMobile ? '120px' : '140px', borderRadius: '8px', backgroundColor: isDark ? '#2a2a2e' : '#fff', overflow: 'hidden', marginBottom: '10px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                          {item.thumbUrl ? (
                            <img src={item.thumbUrl} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                          ) : (
                            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <IItem style={{ fontSize: 32, color: '#ccc' }} />
                            </div>
                          )}
                        </div>
                        {/* Name */}
                        <div style={{ fontSize: '13px', fontWeight: 500, color: textColor, lineHeight: '1.3', minHeight: '2.4em', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {item.name}
                        </div>
                        {/* Stats */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px', marginTop: '8px', gap: '4px', minWidth: 0 }}>
                          <span style={{ color: textColor, fontWeight: 500, whiteSpace: 'nowrap', flexShrink: 0 }}>{parseFloat(item.quantity.toFixed(2))} {item.unit}</span>
                          <span style={{ color: subColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>@ {item.rate > 0 ? item.rate.toFixed(2) : '—'}</span>
                        </div>
                        {/* Value bar */}
                        <div style={{ marginTop: '6px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '3px' }}>
                            <span style={{ color: subColor }}>{item.storeName}</span>
                          </div>
                          <div style={{ height: '4px', borderRadius: '2px', backgroundColor: isDark ? '#444' : '#e2e8f0', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${Math.min((item.value / (topByValue[0]?.value || 1)) * 100, 100)}%`, borderRadius: '2px', backgroundColor: '#10b981' }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {totalPages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', padding: '12px 0 4px' }}>
                    <div onClick={() => topItemsPage > 0 && setTopItemsPage(topItemsPage - 1)} style={{ width: '28px', height: '28px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: topItemsPage > 0 ? 'pointer' : 'default', opacity: topItemsPage > 0 ? 1 : 0.3, backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9', color: isDark ? '#e2e8f0' : '#334155', fontSize: '14px' }}>‹</div>
                    <span style={{ fontSize: '12px', color: subColor, fontWeight: 500 }}>{start + 1}–{Math.min(start + TOP_ITEMS_PER_PAGE, topByValue.length)} of {topByValue.length}</span>
                    <div onClick={() => topItemsPage < totalPages - 1 && setTopItemsPage(topItemsPage + 1)} style={{ width: '28px', height: '28px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: topItemsPage < totalPages - 1 ? 'pointer' : 'default', opacity: topItemsPage < totalPages - 1 ? 1 : 0.3, backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9', color: isDark ? '#e2e8f0' : '#334155', fontSize: '14px' }}>›</div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Slow Moving grid */}
          {itemsTab === 'slow' && (() => {
            const totalPages = Math.ceil(slowMoving.length / TOP_ITEMS_PER_PAGE);
            const start = topItemsPage * TOP_ITEMS_PER_PAGE;
            const visible = slowMoving.slice(start, start + TOP_ITEMS_PER_PAGE);
            return (
              <div>
                <div key={`slow-page-${topItemsPage}`} style={isMobile ? {
                  display: 'flex', gap: '12px', overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: '4px',
                } : {
                  display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px',
                }}>
                  {visible.map((item, i) => {
                    const valueFmt = item.value > 0 ? (item.value >= 1000 ? `${(item.value / 1000).toFixed(1)}K` : item.value.toFixed(0)) : '—';
                    return (
                      <div key={item._id || i} style={{
                        backgroundColor: isDark ? '#333' : '#f8fafc', borderRadius: '10px', border: '1px solid transparent',
                        padding: '10px', display: 'flex', flexDirection: 'column', position: 'relative', transition: 'all 0.2s ease',
                        minWidth: 0, overflow: 'hidden',
                        ...(isMobile ? { minWidth: '160px', flexShrink: 0 } : {}),
                      }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDark ? '#3a3a3e' : '#fff'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; e.currentTarget.style.border = isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e0e7ff'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isDark ? '#333' : '#f8fafc'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.border = '1px solid transparent'; }}
                      >
                        {/* Activity badge */}
                        <div style={{
                          position: 'absolute', top: '8px', right: '8px', zIndex: 1,
                          padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 700,
                          backgroundColor: item.txCount30d === 0 ? '#ef4444' : '#d97706', color: '#fff',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                        }}>
                          {item.txCount30d === 0 ? 'Idle' : `${item.txCount30d} txn`}
                        </div>
                        {/* Image */}
                        <div style={{ width: '100%', height: isMobile ? '120px' : '140px', borderRadius: '8px', backgroundColor: isDark ? '#2a2a2e' : '#fff', overflow: 'hidden', marginBottom: '10px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                          {item.thumbUrl ? (
                            <img src={item.thumbUrl} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                          ) : (
                            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <IItem style={{ fontSize: 32, color: '#ccc' }} />
                            </div>
                          )}
                        </div>
                        {/* Name */}
                        <div style={{ fontSize: '13px', fontWeight: 500, color: textColor, lineHeight: '1.3', minHeight: '2.4em', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {item.name}
                        </div>
                        {/* Stats */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px', marginTop: '8px', gap: '4px', minWidth: 0 }}>
                          <span style={{ color: textColor, fontWeight: 500, whiteSpace: 'nowrap', flexShrink: 0 }}>{parseFloat(item.quantity.toFixed(2))} {item.unit}</span>
                          <span style={{ color: subColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{item.storeName}</span>
                        </div>
                        {/* Value held bar */}
                        <div style={{ marginTop: '6px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '3px' }}>
                            <span style={{ color: subColor }}>Value held</span>
                            <span style={{ fontWeight: 600, color: item.value > 0 ? (isDark ? '#fbbf24' : '#b45309') : subColor }}>{valueFmt}</span>
                          </div>
                          <div style={{ height: '4px', borderRadius: '2px', backgroundColor: isDark ? '#444' : '#e2e8f0', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${Math.min((item.value / (slowMoving[0]?.value || 1)) * 100, 100)}%`, borderRadius: '2px', backgroundColor: '#f59e0b' }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {totalPages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', padding: '12px 0 4px' }}>
                    <div onClick={() => topItemsPage > 0 && setTopItemsPage(topItemsPage - 1)} style={{ width: '28px', height: '28px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: topItemsPage > 0 ? 'pointer' : 'default', opacity: topItemsPage > 0 ? 1 : 0.3, backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9', color: isDark ? '#e2e8f0' : '#334155', fontSize: '14px' }}>‹</div>
                    <span style={{ fontSize: '12px', color: subColor, fontWeight: 500 }}>{start + 1}–{Math.min(start + TOP_ITEMS_PER_PAGE, slowMoving.length)} of {slowMoving.length}</span>
                    <div onClick={() => topItemsPage < totalPages - 1 && setTopItemsPage(topItemsPage + 1)} style={{ width: '28px', height: '28px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: topItemsPage < totalPages - 1 ? 'pointer' : 'default', opacity: topItemsPage < totalPages - 1 ? 1 : 0.3, backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9', color: isDark ? '#e2e8f0' : '#334155', fontSize: '14px' }}>›</div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Reorder items list */}
          {itemsTab === 'reorder' && (
            reorderItems.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px', color: subColor, fontSize: '13px' }}>
                All items are above their reorder levels
              </div>
            ) : (
              <div>
                {/* Column headers */}
                {!isMobile && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '0 12px 8px',
                    fontSize: '10px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: isDark ? 'rgba(255,255,255,0.35)' : '#94a3b8',
                  }}>
                    <div style={{ width: '36px', flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>Item</div>
                    <div style={{ width: '90px', textAlign: 'center', flexShrink: 0 }}>Stock</div>
                    <div style={{ width: '70px', textAlign: 'center', flexShrink: 0 }}>Reorder At</div>
                    <div style={{ width: '60px', textAlign: 'right', flexShrink: 0 }}>Rate</div>
                    <div style={{ width: '80px', textAlign: 'right', flexShrink: 0 }}>Last Activity</div>
                    <div style={{ width: '40px', flexShrink: 0 }} />
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '500px', overflowY: 'auto' }}>
                  {reorderItems.map((item, i) => {
                    const pct = item.reorderLevel > 0 ? Math.min((item.quantity / item.reorderLevel) * 100, 100) : 0;
                    const isOut = item.quantity <= 0;
                    const lastAct = item.lastActivity ? timeAgo(item.lastActivity) : '—';
                    return (
                      <div
                        key={item._id || i}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '8px 12px',
                          borderRadius: '10px',
                          backgroundColor: isDark ? '#333' : '#f8fafc',
                          border: '1px solid transparent',
                          transition: 'all 0.15s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = isDark ? '#3a3a3e' : '#fff';
                          e.currentTarget.style.border = isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = isDark ? '#333' : '#f8fafc';
                          e.currentTarget.style.border = '1px solid transparent';
                        }}
                      >
                        <ItemThumb thumbUrl={item.thumbUrl} size={36} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontSize: '13px',
                            fontWeight: 500,
                            color: textColor,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}>
                            {item.name}
                            {item.code && <span style={{ color: subColor, fontWeight: 400, marginLeft: '6px', fontSize: '11px' }}>({item.code})</span>}
                          </div>
                          <div style={{ fontSize: '11px', color: subColor, marginTop: '1px' }}>
                            {item.storeName}
                          </div>
                        </div>
                        {/* Stock level with bar */}
                        <div style={{ width: '90px', flexShrink: 0 }}>
                          <div style={{
                            fontSize: '13px',
                            fontWeight: 600,
                            color: isOut ? '#ef4444' : '#d97706',
                            textAlign: 'center',
                          }}>
                            {parseFloat(item.quantity.toFixed(2))} <span style={{ fontSize: '10px', fontWeight: 400, color: subColor }}>{item.unit}</span>
                          </div>
                          <div style={{
                            height: '3px',
                            borderRadius: '2px',
                            backgroundColor: isDark ? '#444' : '#e2e8f0',
                            overflow: 'hidden',
                            marginTop: '4px',
                          }}>
                            <div style={{
                              height: '100%',
                              width: `${pct}%`,
                              borderRadius: '2px',
                              backgroundColor: isOut ? '#ef4444' : '#d97706',
                            }} />
                          </div>
                        </div>
                        {/* Reorder level — editable */}
                        <div
                          style={{
                            width: '80px',
                            textAlign: 'center',
                            flexShrink: 0,
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {editingReorder === item._id ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                              <input
                                type="number"
                                value={editReorderVal}
                                onChange={(e) => setEditReorderVal(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') saveReorderLevel(item._id, editReorderVal);
                                  if (e.key === 'Escape') setEditingReorder(null);
                                }}
                                autoFocus
                                style={{
                                  width: '50px',
                                  fontSize: '12px',
                                  padding: '3px 4px',
                                  borderRadius: '4px',
                                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.2)' : '#cbd5e1'}`,
                                  backgroundColor: isDark ? '#2a2a2e' : '#fff',
                                  color: textColor,
                                  textAlign: 'center',
                                  outline: 'none',
                                }}
                              />
                              <div
                                onClick={() => saveReorderLevel(item._id, editReorderVal)}
                                style={{
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  color: '#4f46e5',
                                  cursor: savingReorder === item._id ? 'wait' : 'pointer',
                                  opacity: savingReorder === item._id ? 0.5 : 1,
                                }}
                              >
                                {savingReorder === item._id ? '...' : '✓'}
                              </div>
                            </div>
                          ) : (
                            <div
                              onClick={() => {
                                setEditingReorder(item._id);
                                setEditReorderVal(String(item.reorderLevel));
                              }}
                              style={{
                                fontSize: '12px',
                                fontWeight: 500,
                                color: subColor,
                                cursor: 'pointer',
                                padding: '2px 4px',
                                borderRadius: '4px',
                                transition: 'background-color 0.15s',
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                              title="Click to edit reorder level"
                            >
                              {item.reorderLevel} {item.unit}
                            </div>
                          )}
                        </div>
                        {/* Rate */}
                        {!isMobile && (
                          <div style={{
                            width: '60px',
                            textAlign: 'right',
                            fontSize: '12px',
                            color: item.rate > 0 ? textColor : subColor,
                            flexShrink: 0,
                          }}>
                            {item.rate > 0 ? item.rate.toFixed(2) : '—'}
                          </div>
                        )}
                        {/* Last activity */}
                        {!isMobile && (
                          <div style={{
                            width: '80px',
                            textAlign: 'right',
                            fontSize: '11px',
                            color: subColor,
                            flexShrink: 0,
                          }}>
                            {lastAct}
                          </div>
                        )}
                        {/* Status pill */}
                        <div style={{
                          fontSize: '10px',
                          fontWeight: 600,
                          padding: '3px 8px',
                          borderRadius: '10px',
                          flexShrink: 0,
                          backgroundColor: isOut ? (isDark ? 'rgba(239,68,68,0.15)' : '#fef2f2') : (isDark ? 'rgba(245,158,11,0.15)' : '#fffbeb'),
                          color: isOut ? '#ef4444' : '#d97706',
                        }}>
                          {isOut ? 'Out' : 'Low'}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )
          )}
        </div>
      ) : null}

      {/* Store cards */}
      <div style={{
        backgroundColor: cardBg,
        borderRadius: '12px',
        border: cardBorder,
        boxShadow: cardShadow,
        padding: '20px',
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}>
          <div>
            {stores === null || dashData === null ? (
              <>
                <Sk w="80px" h="16px" r={4} />
                <Sk w="140px" h="12px" r={3} style={{ marginTop: 6 }} delay={0.05} />
              </>
            ) : (
              <>
                <div style={{ fontSize: '16px', fontWeight: 600, color: textColor }}>Stores</div>
                <div style={{ fontSize: '13px', color: subColor, marginTop: '2px' }}>All locations overview</div>
              </>
            )}
          </div>
        </div>
        {stores === null || dashData === null ? (
          <div style={isMobile
            ? { display: 'flex', flexDirection: 'column', gap: '8px' }
            : { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }
          }>
            {[0, 0.08, 0.16, 0.24].map((d, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 14,
                padding: '14px 16px', borderRadius: 12,
                backgroundColor: isDark ? '#333' : '#f8fafc',
              }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 12,
                  backgroundColor: skFrom,
                  animation: `skPulse 2s ease-in-out ${d}s infinite`,
                }} />
                <div style={{ flex: 1 }}>
                  <div style={{
                    width: '75%', height: 13, borderRadius: 4,
                    backgroundColor: skFrom,
                    animation: `skPulse 2s ease-in-out ${d + 0.05}s infinite`,
                  }} />
                  <div style={{
                    width: '45%', height: 10, borderRadius: 3, marginTop: 6,
                    backgroundColor: skFrom,
                    animation: `skPulse 2s ease-in-out ${d + 0.1}s infinite`,
                  }} />
                  <div style={{
                    width: '60%', height: 3, borderRadius: 2, marginTop: 8,
                    backgroundColor: skFrom,
                    animation: `skPulse 2s ease-in-out ${d + 0.15}s infinite`,
                  }} />
                </div>
                <div style={{
                  width: 20, height: 20, borderRadius: 4,
                  backgroundColor: skFrom,
                  animation: `skPulse 2s ease-in-out ${d + 0.1}s infinite`,
                }} />
              </div>
            ))}
          </div>
        ) : stores.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#999', fontSize: '13px' }}>
            No stores available
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {stores.map((store, storeIdx) => {
              const storeColors = [
                'linear-gradient(135deg, #6366f1, #4f46e5)',
                'linear-gradient(135deg, #10b981, #059669)',
                'linear-gradient(135deg, #f59e0b, #d97706)',
                'linear-gradient(135deg, #f43f5e, #e11d48)',
                'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                'linear-gradient(135deg, #06b6d4, #0891b2)',
              ];
              return (
              <div
                key={store._id}
                onClick={() => goToStore(store)}
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  padding: '14px 16px',
                  borderRadius: '12px',
                  backgroundColor: isDark ? '#333' : '#f8fafc',
                  border: '1px solid transparent',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = isDark ? '#3a3a3e' : '#fff';
                  e.currentTarget.style.boxShadow = cardHoverShadow;
                  e.currentTarget.style.border = isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = isDark ? '#333' : '#f8fafc';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.border = '1px solid transparent';
                }}
              >
                <div style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '12px',
                  background: storeColors[storeIdx % storeColors.length],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  color: 'white',
                }}>
                  <IStores style={{ fontSize: 20 }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: textColor,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                    {store.name}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '3px' }}>
                    <span style={{ fontSize: '12px', color: subColor }}>
                      {store.itemCount || 0} items
                      {store.transactionsCount > 0 && ` · ${formatCount(store.transactionsCount)} txns`}
                    </span>
                    {inventoryValue[store._id] > 0 && (
                      <span style={{ fontSize: '11px', color: isDark ? '#ffb74d' : '#e65100', fontWeight: 600 }}>
                        {inventoryValue[store._id] >= 1000000
                          ? `${(inventoryValue[store._id] / 1000000).toFixed(1)}M`
                          : inventoryValue[store._id] >= 1000
                            ? `${(inventoryValue[store._id] / 1000).toFixed(1)}K`
                            : inventoryValue[store._id].toFixed(0)}
                      </span>
                    )}
                  </div>
                  {storeVolume[store._id] > 0 && (() => {
                    const allVolumes = stores.map(s => storeVolume[s._id] || 0);
                    const maxVol = Math.max(...allVolumes, 1);
                    const pct = (storeVolume[store._id] / maxVol) * 100;
                    return (
                      <div style={{
                        height: '3px',
                        borderRadius: '2px',
                        backgroundColor: isDark ? '#444' : '#eee',
                        marginTop: '6px',
                        overflow: 'hidden',
                      }}>
                        <div style={{
                          height: '100%',
                          width: `${pct}%`,
                          backgroundColor: '#556cd6',
                          borderRadius: '2px',
                        }} />
                      </div>
                    );
                  })()}
                </div>
                <IArrowRight style={{ fontSize: 20, color: isDark ? '#666' : '#ccc', flexShrink: 0 }} />
              </div>
              );
            })}
          </div>
        )}
      </div>

      </div>{/* end bottom row grid */}

      {/* Recent Activity */}
      <div style={{ padding: '0 4px' }}>
        <div style={{
          fontSize: '13px',
          fontWeight: 600,
          color: sectionTitle,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          marginBottom: '10px',
          paddingLeft: '2px',
        }}>
          Recent Activity
        </div>
        {recentTx === null ? (
          <div style={{
            backgroundColor: cardBg, borderRadius: 16, border: cardBorder, overflow: 'hidden',
          }}>
            {[0, 0.06, 0.12, 0.18, 0.24].map((d, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                borderBottom: i < 4 ? `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'}` : 'none',
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 8, flexShrink: 0,
                  backgroundColor: skFrom,
                  animation: `skPulse 2s ease-in-out ${d}s infinite`,
                }} />
                <div style={{
                  width: 28, height: 28, borderRadius: 6, flexShrink: 0,
                  backgroundColor: skFrom,
                  animation: `skPulse 2s ease-in-out ${d + 0.03}s infinite`,
                }} />
                <div style={{ flex: 1 }}>
                  <div style={{
                    width: '60%', height: 12, borderRadius: 4,
                    backgroundColor: skFrom,
                    animation: `skPulse 2s ease-in-out ${d + 0.05}s infinite`,
                  }} />
                  <div style={{
                    width: '35%', height: 10, borderRadius: 3, marginTop: 5,
                    backgroundColor: skFrom,
                    animation: `skPulse 2s ease-in-out ${d + 0.08}s infinite`,
                  }} />
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{
                    width: 45, height: 12, borderRadius: 4, marginLeft: 'auto',
                    backgroundColor: skFrom,
                    animation: `skPulse 2s ease-in-out ${d + 0.05}s infinite`,
                  }} />
                  <div style={{
                    width: 30, height: 9, borderRadius: 3, marginTop: 5, marginLeft: 'auto',
                    backgroundColor: skFrom,
                    animation: `skPulse 2s ease-in-out ${d + 0.08}s infinite`,
                  }} />
                </div>
              </div>
            ))}
          </div>
        ) : recentTx.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '16px', color: '#999', fontSize: '13px' }}>
            No recent activity
          </div>
        ) : (
          <div style={{
            backgroundColor: cardBg,
            borderRadius: '12px',
            boxShadow: cardShadow, border: cardBorder,
            overflow: 'hidden',
          }}>
            {recentTx.map((tx, i) => {
              const config = txConfig[tx.transactionType] || txConfig.modify;
              const TxIcon = config.icon;

              return (
                <div
                  key={tx._id || i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 14px',
                    borderBottom: i < recentTx.length - 1
                      ? `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}`
                      : 'none',
                  }}
                >
                  <ItemThumb thumbUrl={tx.thumbUrl} size={40} />
                  <div style={{
                    width: '26px',
                    height: '26px',
                    borderRadius: '6px',
                    backgroundColor: config.color + '15',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <TxIcon size={13} color={config.color} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '13px',
                      fontWeight: 500,
                      color: textColor,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      {tx.itemName}
                    </div>
                    <div style={{ fontSize: '11px', color: subColor, marginTop: '1px' }}>
                      {tx.storeName}{tx.createdBy ? ` · ${tx.createdBy}` : ''}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: config.color,
                    }}>
                      {tx.transactionType === 'in' || tx.transactionType === 'transferIn' ? '+' : '-'}{tx.amount} {tx.unit}
                    </div>
                    <div style={{ fontSize: '10px', color: subColor, marginTop: '1px' }}>
                      {timeAgo(tx.createdAt)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
